import React, { useRef, useState } from "react";
import { Save, X } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import { API_URL } from "../../config";

// ===== RESPONSIVE EDIT EMPLOYEE MODAL COMPONENT =====
const EditEmployeeModal = ({
  styles,
  text,
  language,
  theme,
  screenSize,
  loading,
  selectedEmployee,
  onUpdate,
  onCancel,
  onBackdropClick,
}) => {
  // Initialize form data with selected employee data
  const [formData, setFormData] = useState({
    name: selectedEmployee?.name || "",
    email: selectedEmployee?.email || "",
    phone: selectedEmployee?.phone || "",
    department: selectedEmployee?.department || "",
    laborMode: selectedEmployee?.laborMode || "",
    lastContractTerm:
      selectedEmployee?.lastContractTerm ||
      selectedEmployee?.fullEmployeeData?.lastContractTerm ||
      "",
    company: selectedEmployee?.company || "",
    status: selectedEmployee?.status || "Active",
    salary: selectedEmployee?.salary || "",
    grossSalary: selectedEmployee?.grossSalary || selectedEmployee?.fullEmployeeData?.grossSalary || "",
    salaryInputSource: "",
    insuranceSalary: selectedEmployee?.fullEmployeeData?.insuranceSalary || "",
    nationalId: selectedEmployee?.fullEmployeeData?.nationalId || "",
    address: selectedEmployee?.fullEmployeeData?.address || "",
    city: selectedEmployee?.fullEmployeeData?.city || "",
    country: selectedEmployee?.fullEmployeeData?.country || "Egypt",
    nationality: selectedEmployee?.fullEmployeeData?.nationality || "",
    gender: selectedEmployee?.fullEmployeeData?.gender || "",
    maritalStatus: selectedEmployee?.fullEmployeeData?.maritalStatus || "",
    dateOfBirth: selectedEmployee?.fullEmployeeData?.dateOfBirth
      ? new Date(selectedEmployee.fullEmployeeData.dateOfBirth)
        .toISOString()
        .split("T")[0]
      : "",
  });

  // Form validation errors state
  const [formErrors, setFormErrors] = useState({});

  // Handle input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear error when user starts typing
    if (formErrors[e.target.name]) {
      setFormErrors({
        ...formErrors,
        [e.target.name]: "",
      });
    }
  };

  // Gross/Net salary are mutually derived: whichever one the user types into
  // becomes `salaryInputSource` (sent to the backend as the authoritative
  // figure on save); the other field locks and shows a live preview fetched
  // from the server — same formulas the save itself will use, never
  // duplicated in the frontend.
  const salaryPreviewTimer = useRef(null);
  const handleSalaryFieldChange = (name, value) => {
    const source = name === "grossSalary" ? "gross" : "net";
    setFormData((prev) => ({ ...prev, [name]: value, salaryInputSource: source }));

    if (salaryPreviewTimer.current) clearTimeout(salaryPreviewTimer.current);
    if (value === "" || value === null || isNaN(Number(value))) return;

    salaryPreviewTimer.current = setTimeout(async () => {
      try {
        const response = await apiFetch(`${API_URL}/employees/salary-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grossSalary: source === "gross" ? value : null,
            netSalary: source === "net" ? value : null,
            salaryInputSource: source,
            insuranceSalary: formData.insuranceSalary || null,
          }),
        });
        const data = await response.json();
        if (data?.success) {
          setFormData((prev) => ({
            ...prev,
            grossSalary: source === "gross" ? prev.grossSalary : data.grossSalary ?? prev.grossSalary,
            salary: source === "net" ? prev.salary : data.netSalary ?? prev.salary,
          }));
        }
      } catch (err) {
        console.error("Salary preview failed:", err);
      }
    }, 400);
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    // Check if name is provided
    if (!formData.name.trim()) {
      errors.name = text[language].nameRequired;
    }

    // Check if email is provided and valid
    if (!formData.email.trim()) {
      errors.email = text[language].emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = text[language].emailInvalid;
    }

    // Check if department is provided
    if (!formData.department.trim()) {
      errors.department = text[language].departmentRequired;
    }

    // Update errors state
    setFormErrors(errors);
    // Return true if no errors
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only proceed if form is valid
    if (!validateForm()) {
      return;
    }

    // Call update function
    await onUpdate(formData);
  };

  // Return modal JSX
  return (
    <div
      style={styles.modal}
      onClick={onBackdropClick}
    >
      <div
        style={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{text[language].editEmployee}</h2>
          <button
            style={styles.closeBtn}
            onClick={onCancel}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].basicInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].fullName} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.name
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterFullName}
              />
              {formErrors.name && (
                <span style={styles.errorText}>{formErrors.name}</span>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].emailAddress} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.email
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterEmail}
              />
              {formErrors.email && (
                <span style={styles.errorText}>{formErrors.email}</span>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].phoneNumber}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterPhone}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].nationalId}
              </label>
              <input
                type="text"
                name="nationalId"
                value={formData.nationalId}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterNationalId}
              />
            </div>
          </div>

          {/* Work Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].workInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].department} *
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                style={{
                  ...styles.formSelect,
                  borderColor: formErrors.department
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              >
                <option value="">{text[language].selectDepartment}</option>
                <option value="Information Technology">
                  {text[language].infotech}
                </option>

              </select>
              {formErrors.department && (
                <span style={styles.errorText}>{formErrors.department}</span>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].laborMode}
              </label>
              <select
                name="laborMode"
                value={formData.laborMode}
                onChange={handleInputChange}
                style={styles.formSelect}
              >
                <option value="">{text[language].selectLaborMode}</option>
                <option value="Insource">{text[language].insource}</option>
                <option value="Outsource">{text[language].outsource}</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].lastContractTerm}
              </label>
              <select
                name="lastContractTerm"
                value={formData.lastContractTerm}
                onChange={handleInputChange}
                style={styles.formSelect}
              >
                <option value="">{text[language].selectLastContractTerm}</option>
                <option value="Full-time">{text[language].fullTime}</option>
                <option value="Part-time">{text[language].partTime}</option>
                <option value="Contract">{text[language].contract}</option>
                <option value="Temporary">{text[language].temporary}</option>
                <option value="Freelance">{text[language].freelance}</option>
              </select>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].companyName}
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterCompany}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].statusLabel}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={styles.formSelect}
              >
                <option value="Active">{text[language].statusActive}</option>
                <option value="OnLeave">
                  {text[language].statusOnLeave}
                </option>
                <option value="Inactive">
                  {text[language].statusInactive}
                </option>
              </select>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {language === "ar" ? "الراتب الإجمالي" : "Gross Salary"}
              </label>
              <input
                type="number"
                name="grossSalary"
                value={formData.grossSalary}
                onChange={(e) => handleSalaryFieldChange("grossSalary", e.target.value)}
                style={styles.formInput}
                placeholder={language === "ar" ? "أدخل الراتب الإجمالي" : "Enter gross salary"}
                min="0"
                step="any"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].salaryLabel}
              </label>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={(e) => handleSalaryFieldChange("salary", e.target.value)}
                style={styles.formInput}
                placeholder={text[language].enterSalary}
                min="0"
                step="any"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].gender}</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                style={styles.formSelect}
              >
                <option value="">{text[language].selectGender}</option>
                <option value="Male">{text[language].male}</option>
                <option value="Female">{text[language].female}</option>
              </select>
            </div>
          </div>

          {/* Personal Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].personalInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].dateOfBirth}
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                style={styles.formInput}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].maritalStatus}
              </label>
              <select
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={handleInputChange}
                style={styles.formSelect}
              >
                <option value="">{text[language].selectMaritalStatus}</option>
                <option value="Single">{text[language].single}</option>
                <option value="Married">{text[language].married}</option>
                <option value="Divorced">{text[language].divorced}</option>
                <option value="Widowed">{text[language].widowed}</option>
              </select>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].nationality}
              </label>
              <input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterNationality}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].city}</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterCity}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{text[language].address}</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={styles.formInput}
              placeholder={text[language].enterAddress}
            />
          </div>

          {/* Modal Actions */}
          <div style={styles.modalActions}>
            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={onCancel}
            >
              {text[language].cancel}
            </button>
            <button
              type="submit"
              style={{
                ...styles.primaryBtn,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
            >
              <Save size={screenSize === "xs" ? 14 : 16} />
              {loading ? text[language].updating : text[language].update}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployeeModal;
